import {useState} from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Collapse,
    Grid,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    Typography
} from "@mui/material";
import {usePessoasOcorrencia} from "../../api/DpgeIntegracaoEsfingeApi";

export function eventoPossuiPessoas(evento = "") {
    return (
        /^(FOLHA_PAGAMENTO|SERVIDORES|DADOS_FUNCIONAIS_ATIVOS|INATIVOS|PENSIONISTAS)_(INICIAR|ENVIAR_PARCIAL|FINALIZAR)$/.test(
            evento
        ) ||
        [
            "INGRESSO_ESTATUTARIO",
            "INGRESSO_CONTRATO",
            "ALTERACAO_POSSE",
            "CONCURSO_ENVIAR"
        ].includes(evento)
    );
}

const vazios = {matricula: "", cpf: "", nome: ""};
export default function PessoasOcorrencia({ocorrenciaId}) {
    const [entrada, setEntrada] = useState(vazios);
    const [filtros, setFiltros] = useState({...vazios, pagina: 0, tamanho: 25});
    const {data, isLoading, isError, refetch} = usePessoasOcorrencia(
        ocorrenciaId,
        filtros
    );
    function filtrar(event) {
        event.preventDefault();
        setFiltros({...entrada, pagina: 0, tamanho: 25});
    }
    return (
        <Stack spacing={2}>
            <Typography variant="h6">
                Conferência das pessoas da ocorrência
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Consulte a fotografia preparada para envio. Os filtros apenas
                alteram a visualização.
            </Typography>
            <Box component="form" onSubmit={filtrar}>
                <Grid container spacing={2}>
                    {[
                        ["matricula", "Matrícula"],
                        ["cpf", "CPF"],
                        ["nome", "Nome"]
                    ].map(([campo, label]) => (
                        <Grid
                            item
                            xs={12}
                            md={campo === "nome" ? 6 : 3}
                            key={campo}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                label={label}
                                value={entrada[campo]}
                                inputProps={{
                                    maxLength: campo === "nome" ? 200 : 30
                                }}
                                onChange={(event) =>
                                    setEntrada({
                                        ...entrada,
                                        [campo]: event.target.value
                                    })
                                }
                            />
                        </Grid>
                    ))}
                </Grid>
                <Stack direction="row" spacing={1} mt={2}>
                    <Button type="submit" variant="contained">
                        Filtrar pessoas
                    </Button>
                    <Button
                        onClick={() => {
                            setEntrada(vazios);
                            setFiltros({...vazios, pagina: 0, tamanho: 25});
                        }}
                    >
                        Limpar filtros
                    </Button>
                </Stack>
            </Box>
            {isLoading && (
                <CircularProgress size={24} aria-label="Carregando pessoas" />
            )}
            {isError && (
                <Alert
                    severity="error"
                    action={
                        <Button onClick={() => refetch()}>
                            Tentar novamente
                        </Button>
                    }
                >
                    Não foi possível consultar as pessoas desta ocorrência.
                </Alert>
            )}
            {data && (
                <>
                    {data.dadosDoFluxo && (
                        <Alert severity="info">
                            Esta etapa técnica não contém pessoas. A conferência
                            usa o snapshot da ocorrência raiz #
                            {data.origemPayloadId}.
                        </Alert>
                    )}
                    <Typography variant="body2">
                        {data.totalFiltrados} de {data.totalRegistros} registros
                        da ocorrência #{data.origemPayloadId}.
                    </Typography>
                    {!data.totalFiltrados ? (
                        <Alert severity="info">
                            Nenhuma pessoa encontrada com os filtros informados.
                        </Alert>
                    ) : (
                        <TableContainer>
                            <Table
                                size="small"
                                aria-label="Pessoas da ocorrência"
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Matrícula</TableCell>
                                        <TableCell>CPF</TableCell>
                                        <TableCell>Nome</TableCell>
                                        <TableCell>Conferência</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.registros.map((pessoa, index) => (
                                        <PessoaLinha
                                            key={filtros.pagina + "-" + index}
                                            pessoa={pessoa}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    <TablePagination
                        component="div"
                        count={data.totalFiltrados}
                        page={filtros.pagina}
                        rowsPerPage={25}
                        rowsPerPageOptions={[25]}
                        onPageChange={(_, pagina) =>
                            setFiltros({...filtros, pagina})
                        }
                        labelDisplayedRows={({from, to, count}) =>
                            from + "–" + to + " de " + count
                        }
                        getItemAriaLabel={(tipo) =>
                            tipo === "next"
                                ? "Próxima página"
                                : "Página anterior"
                        }
                    />
                    <Typography variant="caption" color="text.secondary">
                        Quando o nome não integra o payload, a identificação
                        consultada é indicada abaixo dele. Os detalhes preservam
                        os dados originais enviados.
                    </Typography>
                </>
            )}
        </Stack>
    );
}

function PessoaLinha({pessoa}) {
    const [aberto, setAberto] = useState(false);
    return (
        <>
            <TableRow>
                <TableCell>{pessoa.matricula || "Não consta"}</TableCell>
                <TableCell>{pessoa.cpf}</TableCell>
                <TableCell>
                    {pessoa.nome || "Nome não disponível no histórico"}
                    {pessoa.origemNomeId && (
                        <Typography variant="caption" display="block">
                            Identificação da ocorrência #{pessoa.origemNomeId}
                        </Typography>
                    )}
                </TableCell>
                <TableCell>
                    <Button
                        size="small"
                        aria-expanded={aberto}
                        onClick={() => setAberto(!aberto)}
                    >
                        {aberto ? "Fechar detalhes" : "Ver detalhes"}
                    </Button>
                </TableCell>
            </TableRow>
            {aberto && (
                <TableRow>
                    <TableCell colSpan={4}>
                        <Collapse in={aberto}>
                            <DadosPessoa dados={pessoa.dados} />
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
        </>
    );
}

const rotulos = {
    numeroCPF: "CPF",
    numeroCpf: "CPF",
    numeroMatricula: "Matrícula",
    numerodaMatricula: "Matrícula",
    codigoCargo: "Código do cargo",
    descricaoLotacao: "Lotação",
    nomeServidor: "Nome",
    codigoTipoMovimentacao: "Tipo de movimentação",
    codigoTipoVinculo: "Tipo de vínculo",
    dataInicioVigenciaVinculo: "Início da vigência do vínculo",
    dataInicioVigenciaCargo: "Início da vigência do cargo",
    codigoUnidadeOrcamentaria: "Unidade orçamentária",
    codigoTipoQuadroPessoal: "Tipo de quadro",
    codigoBanco: "Banco",
    codigoAgencia: "Agência",
    codigoConta: "Conta",
    codigoComponente: "Componente",
    codigoComponenteFolha: "Componente",
    indicativoProventoDesconto: "Provento/desconto",
    valorComponente: "Valor",
    lancamentos: "Lançamentos"
};
function rotulo(chave) {
    return rotulos[chave] || chave.replace(/([a-z])([A-Z])/g, "$1 $2");
}
function DadosPessoa({dados}) {
    return (
        <Stack spacing={2}>
            <Grid container spacing={1}>
                {Object.entries(dados || {})
                    .filter(([, v]) => v !== null && typeof v !== "object")
                    .map(([chave, valor]) => (
                        <Grid item xs={12} sm={6} md={4} key={chave}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {rotulo(chave)}
                            </Typography>
                            <Typography variant="body2">
                                {String(valor)}
                            </Typography>
                        </Grid>
                    ))}
            </Grid>
            {Object.entries(dados || {})
                .filter(([, v]) => Array.isArray(v))
                .map(([chave, linhas]) => (
                    <Box key={chave}>
                        <Typography variant="subtitle2">
                            {rotulo(chave)} ({linhas.length})
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        {Object.keys(linhas[0] || {}).map(
                                            (k) => (
                                                <TableCell key={k}>
                                                    {rotulo(k)}
                                                </TableCell>
                                            )
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {linhas.map((linha, i) => (
                                        <TableRow key={i}>
                                            {Object.keys(linhas[0] || {}).map(
                                                (k) => (
                                                    <TableCell key={k}>
                                                        {k === "valorComponente"
                                                            ? Number(
                                                                  linha[k]
                                                              ).toLocaleString(
                                                                  "pt-BR",
                                                                  {
                                                                      style: "currency",
                                                                      currency:
                                                                          "BRL"
                                                                  }
                                                              )
                                                            : String(
                                                                  linha[k] ?? ""
                                                              )}
                                                    </TableCell>
                                                )
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                ))}
        </Stack>
    );
}
